import axiosInstance from './axios';
import { baseUrl } from './constants';

const USDollar: Intl.NumberFormat = new Intl.NumberFormat(`en-US`, {
    style: `currency`,
    currency: `USD`,
});

interface NamedItem {
    name: string;
}

export const searchByName = <T extends NamedItem>(listData: T[], searchValue: string): T[] => {
    if (searchValue.length > 0) {
        const searchResult: T[] = listData.filter(item =>
            item.name.toLocaleLowerCase().match(searchValue.toLowerCase())
        );
        return searchResult;
    }
    return listData;
};
export const getRoute = (pathname: string): string => {
    const str: string[] = pathname.split(`/`);
    return `/${str[1]}`;
};
export const localSet = (key: string, item: string): void => {
    localStorage.setItem(key, item);
    window.dispatchEvent(new Event(`storage`));
};

export const clearLocalStorage = (): void => {
    const settings: string | null = localStorage.getItem(`settings`);
    localStorage.clear();
    if (settings) {
        localStorage.setItem(`settings`, settings);
    }
};

export const dayTime = (): string => {
    const curHr: number = new Date().getHours();

    if (curHr < 12) {
        return `morning`;
    } else if (curHr < 18) {
        return `afternoon`;
    } else {
        return `evening`;
    }
};

export const formatMoney = (v: number): string => USDollar.format(v);

interface FormatDateOpts {
    shortDate?: boolean;
}

export const formatDate = (d: string | number | Date | null | undefined, opts?: FormatDateOpts): string =>
    d
        ? new Date(d)
              .toLocaleDateString(`en-US`, {
                  month: `2-digit`,
                  day: `2-digit`,
                  year: opts?.shortDate ? `2-digit` : `numeric`,
              })
              .replace(/\//g, `-`)
        : ``;

export const getMonthName = (monthNumber: number): string => {
    const date: Date = new Date();
    date.setMonth(monthNumber);
    return date.toLocaleString(`default`, { month: `long` });
};

interface UploadImageOptions {
    p?: string;
    o?: string;
    loc?: string;
    key?: string;
}

interface SignedUrlResponse {
    signedUrl?: string;
    viewUrl: string;
    error?: string;
}

export const uploadImage = (img: Blob, { p, o, loc, key }: UploadImageOptions): Promise<string> =>
    axiosInstance
        .get<SignedUrlResponse>(
            `${baseUrl}signedurl?${key ? `key=${encodeURIComponent(key)}` : `p=${p}&o=${o}`}${loc ? `&loc=${loc}` : ``}`
        )
        .then(r => {
            if (!r.data?.signedUrl) {
                throw new Error(r.data?.error || `The image could not be uploaded`);
            }
            const myHeaders: Headers = new Headers({ 'Content-Type': `image/*` });
            return fetch(r.data.signedUrl, {
                method: `PUT`,
                headers: myHeaders,
                body: img,
            }).then((put: Response) => {
                if (!put.ok) {
                    throw new Error(`The image could not be uploaded`);
                }
                return r.data.viewUrl;
            });
        });

export const uploadTextFile = (textfile: string, { loc, key }: UploadImageOptions): Promise<boolean> =>
    axiosInstance
        .get<SignedUrlResponse>(
            `${baseUrl}signedurl?${key ? `key=${encodeURIComponent(key)}` : ``}${loc ? `&loc=${loc}` : ``}`
        )
        .then(r => {
            if (!r.data?.signedUrl) {
                throw new Error(r.data?.error || `The data could not be uploaded`);
            }
            const myHeaders: Headers = new Headers({ 'Content-Type': `application/json` });
            return fetch(r.data.signedUrl, {
                method: `PUT`,
                headers: myHeaders,
                body: textfile,
            }).then((put: Response) => {
                if (!put.ok) {
                    throw new Error(`The data could not be uploaded`);
                }
                return true;
            });
        });

interface FileShareUploadOptions {
    recipientPortalUserId: number;
    jiraTicket?: string;
}

interface InitUploadResponse {
    fileShareGuid: string;
    uploadUrl: string;
}

/**
 * Three steps, because the file never passes through the API: ask the backend
 * for a signed PUT, send the bytes straight to S3, then tell the backend the
 * object landed so it becomes visible to the recipient. Until that last call
 * the share stays pending and the recipient sees nothing.
 *
 * The Content-Type header has to match the mimeType sent to init-upload -- it
 * is signed into the url, and S3 rejects the PUT if it differs. Same contract
 * as uploadImage above.
 */
export const uploadFileShare = async (
    file: File,
    { recipientPortalUserId, jiraTicket }: FileShareUploadOptions
): Promise<string> => {
    const mimeType: string = file.type || `application/octet-stream`;
    const init = await axiosInstance.post<InitUploadResponse>(`${baseUrl}file-shares/init-upload`, {
        originalFilename: file.name,
        fileSizeBytes: file.size,
        mimeType,
        recipientPortalUserId,
        jiraTicket,
    });

    const put: Response = await fetch(init.data.uploadUrl, {
        method: `PUT`,
        headers: new Headers({ 'Content-Type': mimeType }),
        body: file,
    });
    if (!put.ok) {
        throw new Error(`The file could not be uploaded`);
    }

    await axiosInstance.post(`${baseUrl}file-shares/${init.data.fileShareGuid}/confirm`, {});
    return init.data.fileShareGuid;
};
